import type {
  InstructionHandbookSectionConfig,
  InstructionHandbookSectionRole,
} from "./types";

/**
 * Section configurations for instruction handbook document generation (ISO 20607:2019).
 * Content-only sections; excludes form elements (title page, table of contents, index, annexes).
 * Section ids and titles stay here. Model task text is rendered by the server prompt bundle.
 * How to use the product RAG tool is defined in the server instruction-handbook agent system prompt, not repeated here.
 */
export const INSTRUCTION_HANDBOOK_SECTION_CONFIGS: InstructionHandbookSectionConfig[] =
  [
    {
      section: "1",
      title: "Introduction / purpose of this instruction handbook"
    },
    {
      section: "2",
      title: "Safety"
    },
    {
      section: "3",
      title: "Machine overview"
    },
    {
      section: "4",
      title: "Transportation, handling and storage"
    },
    {
      section: "5",
      title: "Assembly, installation and commissioning"
    },
    {
      section: "6",
      title: "Original equipment manufacturer settings"
    },
    {
      section: "7",
      title: "Operation"
    },
    {
      section: "8",
      title: "Product or capacity changeover"
    },
    {
      section: "9",
      title: "Inspection, testing and maintenance"
    },
    {
      section: "10",
      title: "Cleaning and sanitizing"
    },
    {
      section: "11",
      title: "Fault finding / troubleshooting and repair"
    },
    {
      section: "12",
      title: "Dismantling, disabling and scrapping"
    },
    {
      section: "13",
      title: "Documents and drawings"
    },
  ];

/** Canonical section ids in handbook order (ISO 20607 content sections 5.2.1–5.2.13). */
export const INSTRUCTION_HANDBOOK_SECTION_IDS: string[] =
  INSTRUCTION_HANDBOOK_SECTION_CONFIGS.map((config) => config.section);

interface BaseSectionEntry {
  section: string;
  parentSection?: string;
  role?: "headerOnly";
}

const BASE_SECTION_ENTRIES: BaseSectionEntry[] =
  INSTRUCTION_HANDBOOK_SECTION_CONFIGS.map((config) => ({
    section: config.section,
  }));

function resolveBaseSectionRole(
  entry: BaseSectionEntry,
): InstructionHandbookSectionRole {
  if (entry.role === "headerOnly") {
    return "headerOnly";
  }
  if (entry.parentSection != null) {
    return "subsection";
  }
  return "content";
}

const INSTRUCTION_HANDBOOK_SECTION_ROLE_BY_ID = new Map<
  string,
  InstructionHandbookSectionRole
>(
  BASE_SECTION_ENTRIES.map((entry) => [
    entry.section,
    resolveBaseSectionRole(entry),
  ]),
);

/** Manifest role for a canonical instruction handbook section id. */
export function getInstructionHandbookSectionRole(
  sectionId: string,
): InstructionHandbookSectionRole {
  return INSTRUCTION_HANDBOOK_SECTION_ROLE_BY_ID.get(sectionId) ?? "content";
}

export function isInstructionHandbookHeaderOnlySection(
  sectionId: string,
): boolean {
  return getInstructionHandbookSectionRole(sectionId) === "headerOnly";
}

export function isInstructionHandbookSubsectionSection(
  sectionId: string,
): boolean {
  return getInstructionHandbookSectionRole(sectionId) === "subsection";
}

/**
 * When partial regen targets a headerOnly parent, include child section ids.
 * v1 manifest is flat 1–13; expansion is a no-op until parent/child entries exist.
 */
export function expandInstructionHandbookSectionsToGenerate(
  sectionsToGenerate: string[] | undefined,
): string[] | undefined {
  if (sectionsToGenerate == null) {
    return undefined;
  }

  const expanded = new Set(sectionsToGenerate);
  for (const entry of BASE_SECTION_ENTRIES) {
    if (entry.role === "headerOnly" && expanded.has(entry.section)) {
      for (const child of BASE_SECTION_ENTRIES) {
        if (child.parentSection === entry.section) {
          expanded.add(child.section);
        }
      }
    }
  }

  return INSTRUCTION_HANDBOOK_SECTION_IDS.filter((sectionId) =>
    expanded.has(sectionId),
  );
}

export function getPartiallyCompletedMachineSectionConfig(
  config: InstructionHandbookSectionConfig,
  _isPartlyCompletedMachine: boolean | null | undefined,
): InstructionHandbookSectionConfig {
  return config;
}

/** Maps ISO handbook section number to stable extraction / agent registry key. */
const INSTRUCTION_HANDBOOK_EXTRACTION_SECTION_KEY_BY_SECTION: Record<
  string,
  string
> = {
  "1": "introductionPurpose",
  "2": "safety",
  "3": "machineOverview",
  "4": "transportationHandlingStorage",
  "5": "assemblyInstallationCommissioning",
  "6": "oemSettings",
  "7": "operation",
  "8": "productCapacityChangeover",
  "9": "inspectionTestingMaintenance",
  "10": "cleaningSanitizing",
  "11": "faultFindingRepair",
  "12": "dismantlingDisablingScrapping",
  "13": "documentsDrawings",
};

/**
 * Section list for information-for-use extraction prompts.
 * `sectionKey` values align with server `gatewayAgents.instructionHandbookSectionAgents` section ids.
 */
export const INSTRUCTION_HANDBOOK_SECTION_LIST_FOR_EXTRACTION: Array<{
  sectionKey: string;
  section: string;
  title: string;
}> = INSTRUCTION_HANDBOOK_SECTION_CONFIGS.map((config) => ({
  sectionKey:
    INSTRUCTION_HANDBOOK_EXTRACTION_SECTION_KEY_BY_SECTION[config.section],
  section: config.section,
  title: config.title,
}));
